import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { fetchAgentActions, fetchCompanyDetails, archivePage, unarchivePage, queueScrape, updatePricingUrl, fetchTier1Slugs, type AgentAction, type TriageMetadata } from '@/utils/externalDiffsAPI';

const RECENTLY_ARCHIVED_KEY = 'ps_recently_archived';

function addToRecentlyArchived(slug: string, name?: string) {
  try {
    const raw = sessionStorage.getItem(RECENTLY_ARCHIVED_KEY);
    const existing: Array<{ slug: string; name: string; archivedAt: string }> = raw ? JSON.parse(raw) : [];
    const updated = [{ slug, name: name ?? slug, archivedAt: new Date().toISOString() }, ...existing.filter(x => x.slug !== slug)].slice(0, 20);
    sessionStorage.setItem(RECENTLY_ARCHIVED_KEY, JSON.stringify(updated));
  } catch {}
}

function removeFromRecentlyArchived(slug: string) {
  try {
    const raw = sessionStorage.getItem(RECENTLY_ARCHIVED_KEY);
    const existing: Array<{ slug: string; name: string; archivedAt: string }> = raw ? JSON.parse(raw) : [];
    sessionStorage.setItem(RECENTLY_ARCHIVED_KEY, JSON.stringify(existing.filter(x => x.slug !== slug)));
  } catch {}
}

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function BucketBadge({ bucket }: { bucket: 1 | 2 | 3 }) {
  if (bucket === 1) return <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">Bucket 1 — Scheduled</span>;
  return <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">Needs Review</span>;
}

const BUCKET_REASON_LABELS: Record<string, string> = {
  never_queued: 'Never queued this week',
  credits_depleted: 'Usage credits depleted (resets tonight)',
  transient_network: 'Transient network error (ETIMEDOUT / connection closed)',
  task_timeout: 'Task timed out (>30 min)',
  vault_upload_failure: 'Scrape succeeded but Vault upload failed',
  low_quality_content: 'Scraped content too low quality (1–2/10) — pricing URL likely stale or page has no public pricing',
  url_dead_404: 'Pricing page URL returns 404 — page no longer exists. Recommend archiving.',
  url_forbidden_403: 'Pricing page URL returns 403 — access blocked. May require login or URL update.',
  all_scrapers_failed: 'All scrapers failed — firecrawl, local-claude, and chrome-extension all returned errors',
  cloudflare_captcha: 'Cloudflare / CAPTCHA bot protection blocked all scrapers',
  ocr_api_required: 'Google Vision OCR API required to process image-heavy pricing page',
};

export default function DashboardScrapeDetail() {
  const { slug } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();

  // Reset all action state when slug changes — React Router reuses this component instance
  useEffect(() => {
    setArchived(false);
    setUndoSecondsLeft(0);
    setNextSlug(null);
    setRetrying(false);
    setRetried(false);
    setUrlEditMode(false);
    setUrlInput('');
    setUrlSaving(false);
    setUrlSaved(false);
    setSnoozing(false);
    setSnoozed(false);
    setActionError(null);
    setUndoing(false);
  }, [slug]);

  const { data: actionsRes, isLoading: actionsLoading } = useQuery({
    queryKey: ['triage-action', slug],
    queryFn: () => fetchAgentActions({ slug: slug!, stage: 'triage', limit: 10 }),
    enabled: !!slug,
    staleTime: 2 * 60 * 1000,
  });

  const { data: history } = useQuery({
    queryKey: ['agent-actions-history', slug],
    queryFn: () => fetchAgentActions({ slug: slug!, limit: 20 }),
    enabled: !!slug,
    staleTime: 2 * 60 * 1000,
  });

  const { data: company } = useQuery({
    queryKey: ['company-details', slug],
    queryFn: () => fetchCompanyDetails(slug!),
    enabled: !!slug,
    staleTime: 10 * 60 * 1000,
  });

  const { data: tier1Slugs } = useQuery({
    queryKey: ['dashboard-tier1-slugs'],
    queryFn: fetchTier1Slugs,
    staleTime: 10 * 60 * 1000,
  });

  const isTier1 = tier1Slugs ? tier1Slugs.includes(slug ?? '') : null;

  const latestTriage = actionsRes?.data?.[0] as (AgentAction & { metadata: TriageMetadata }) | undefined;
  const meta = latestTriage?.metadata;

  const pricingUrl = meta?.pricing_url ?? company?.pages?.[0]?.pricing_page_url;

  const [archiving, setArchiving] = useState(false);
  const [archived, setArchived] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoSecondsLeft, setUndoSecondsLeft] = useState(0);
  const [nextSlug, setNextSlug] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retried, setRetried] = useState(false);
  const [urlEditMode, setUrlEditMode] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlSaving, setUrlSaving] = useState(false);
  const [urlSaved, setUrlSaved] = useState(false);
  const [snoozing, setSnoozing] = useState(false);
  const [snoozed, setSnoozed] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!archived || undoSecondsLeft <= 0) return;
    const t = setTimeout(() => setUndoSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [archived, undoSecondsLeft]);

  function getNextEscalatedSlug(currentSlug: string): string | null {
    const scrapeStats = queryClient.getQueryData(['dashboard-scrape-stats']) as any;
    const triageMap = queryClient.getQueryData(['dashboard-triage-actions']) as Map<string, any> | undefined;
    if (!scrapeStats?.outstanding_slugs || !triageMap) return null;
    const needsReview = scrapeStats.outstanding_slugs.filter(
      (s: string) => s !== currentSlug && (triageMap.get(s)?.metadata?.bucket === 2 || triageMap.get(s)?.metadata?.bucket === 3)
    );
    return needsReview[0] ?? null;
  }

  async function handleArchive() {
    if (!slug || archiving || archived) return;
    setArchiving(true);
    setActionError(null);
    try {
      await archivePage(slug);
      const next = getNextEscalatedSlug(slug);
      setNextSlug(next);
      addToRecentlyArchived(slug, company?.name);
      // Optimistically remove from outstanding list
      queryClient.setQueryData(['dashboard-scrape-stats'], (old: any) => {
        if (!old?.outstanding_slugs) return old;
        return { ...old, outstanding_slugs: old.outstanding_slugs.filter((s: string) => s !== slug), outstanding_count: Math.max(0, (old.outstanding_count ?? 1) - 1) };
      });
      setArchived(true);
      setUndoSecondsLeft(30);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Archive failed');
    } finally {
      setArchiving(false);
    }
  }

  async function handleUndo() {
    if (!slug || undoing) return;
    setUndoing(true);
    setActionError(null);
    try {
      await unarchivePage(slug);
      removeFromRecentlyArchived(slug);
      // Restore into outstanding list
      queryClient.setQueryData(['dashboard-scrape-stats'], (old: any) => {
        if (!old) return old;
        return { ...old, outstanding_slugs: [slug, ...(old.outstanding_slugs ?? [])], outstanding_count: (old.outstanding_count ?? 0) + 1 };
      });
      setArchived(false);
      setUndoSecondsLeft(0);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Undo failed');
    } finally {
      setUndoing(false);
    }
  }

  async function handleRetry() {
    if (!slug || retrying || retried) return;
    setRetrying(true);
    setActionError(null);
    try {
      await queueScrape(slug);
      setRetried(true);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }

  async function handleUpdateUrl() {
    if (!slug || urlSaving || !urlInput.trim()) return;
    setUrlSaving(true);
    setActionError(null);
    try {
      await updatePricingUrl(slug, urlInput.trim());
      setUrlSaved(true);
      setUrlEditMode(false);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'URL update failed');
    } finally {
      setUrlSaving(false);
    }
  }

  async function handleSnooze() {
    if (!slug || snoozing || snoozed) return;
    setSnoozing(true);
    setActionError(null);
    try {
      const base = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL || 'https://nyqqynbtcrqdmhezmhsp.supabase.co/functions/v1/management';
      const key = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY || '';
      const res = await fetch(`${base}/agent-actions/snooze`, {
        method: 'POST',
        headers: { 'x-api-key': key, 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, stage: 'triage', hours: 24 }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSnoozed(true);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Snooze failed');
    } finally {
      setSnoozing(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/dashboard" className="text-sm text-gray-400 hover:text-gray-600">← Dashboard</Link>
          <span className="text-gray-300">/</span>
          <Link to="/dashboard/scrapes" className="text-sm text-gray-400 hover:text-gray-600">Outstanding Scrapes</Link>
          <span className="text-gray-300">/</span>
          <span className="text-sm font-semibold text-gray-900 font-mono">{slug}</span>
        </div>

        {actionsLoading ? (
          <div className="space-y-4">
            <div className="h-8 bg-gray-200 rounded animate-pulse w-48" />
            <div className="h-32 bg-gray-200 rounded animate-pulse" />
          </div>
        ) : !latestTriage ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <p className="text-gray-500 mb-2">No triage record found for <span className="font-mono font-semibold">{slug}</span>.</p>
            <p className="text-sm text-gray-400">The heartbeat hasn't processed this slug yet. Run the triage heartbeat to classify it.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900 font-mono">{slug}</h1>
                  {company && <span className="text-base text-gray-500">{company.name}</span>}
                  {isTier1 !== null && (
                    <span className={`text-sm font-bold px-3 py-1 rounded-full ${isTier1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {isTier1 ? 'Tier 1' : 'Tier 2'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {meta && <BucketBadge bucket={meta.bucket} />}
                  {pricingUrl && (
                    <a href={pricingUrl} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-blue-600 hover:underline font-mono truncate max-w-xs">
                      {pricingUrl}
                    </a>
                  )}
                </div>
              </div>
              <div className="text-right text-xs text-gray-400">
                <p>Last processed: <span className="font-medium text-gray-600">{timeAgo(latestTriage.created_at)}</span></p>
                <p className="mt-0.5">{new Date(latestTriage.created_at).toLocaleString()}</p>
                {meta?.cycle_id && <p className="mt-0.5 font-mono">Cycle: {meta.cycle_id}</p>}
              </div>
            </div>

            {/* Diagnosis */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Diagnosis</h2>
              <div className="space-y-4">
                {meta?.bucket_reason && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Reason</p>
                    <p className="text-sm text-gray-800">{BUCKET_REASON_LABELS[meta.bucket_reason] ?? meta.bucket_reason}</p>
                  </div>
                )}
                {latestTriage.reason && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Details</p>
                    <p className="text-sm text-gray-700 font-mono bg-gray-50 rounded px-3 py-2 break-all">{latestTriage.reason}</p>
                  </div>
                )}
                {meta?.last_error && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Last error</p>
                    <p className="text-sm text-red-600 font-mono bg-red-50 rounded px-3 py-2 break-all">{meta.last_error}</p>
                  </div>
                )}
                {meta?.quality_score !== undefined && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Content quality score</p>
                    <p className="text-sm text-amber-600 font-semibold">{meta.quality_score}/10</p>
                  </div>
                )}
                {meta?.scraper_errors && Object.keys(meta.scraper_errors).length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Scraper breakdown</p>
                    <div className="space-y-1.5">
                      {Object.entries(meta.scraper_errors).map(([scraper, err]) => (
                        <div key={scraper} className="flex items-start gap-3 text-sm">
                          <span className="font-mono text-gray-500 w-32 flex-shrink-0">{scraper}</span>
                          <span className="text-red-500 break-all">{err}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Evidence */}
            {meta?.evidence && meta.evidence.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Evidence & Links</h2>
                <div className="flex flex-wrap gap-3">
                  {meta.evidence.map((ev, i) => (
                    <a key={i} href={ev.url} target={ev.url.startsWith('http') ? '_blank' : undefined}
                       rel={ev.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                       className="inline-flex items-center gap-1.5 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-100 transition-colors">
                      {ev.url.startsWith('http') ? '↗' : '→'} {ev.label}
                    </a>
                  ))}
                  {pricingUrl && !meta.evidence.some(e => e.url === pricingUrl) && (
                    <a href={pricingUrl} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-100 transition-colors">
                      ↗ Open pricing page
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Post-archive banner */}
            {archived && (
              <div className="bg-gray-900 text-white rounded-xl px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">✓ <span className="font-mono">{slug}</span> archived</span>
                  {undoSecondsLeft > 0 && (
                    <button
                      onClick={handleUndo}
                      disabled={undoing}
                      className="text-sm text-amber-300 hover:text-amber-200 underline disabled:opacity-50"
                    >
                      {undoing ? 'Undoing…' : `Undo (${undoSecondsLeft}s)`}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {nextSlug ? (
                    <Link
                      to={`/dashboard/scrapes/${nextSlug}`}
                      className="text-sm font-medium text-white bg-blue-600 rounded-lg px-4 py-1.5 hover:bg-blue-700 transition-colors"
                    >
                      Next: {nextSlug} →
                    </Link>
                  ) : (
                    <Link
                      to="/dashboard/scrapes"
                      className="text-sm font-medium text-white bg-blue-600 rounded-lg px-4 py-1.5 hover:bg-blue-700 transition-colors"
                    >
                      ← Back to list
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            {meta && meta.bucket > 1 && !archived && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Actions</h2>
                <div className="flex flex-wrap gap-3">
                  {(meta.bucket === 2 || meta.bucket === 3) && (
                    retried ? (
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                        Queued for scrape
                      </span>
                    ) : (
                      <button
                        onClick={handleRetry}
                        disabled={retrying}
                        className="inline-flex items-center gap-2 text-sm font-medium text-white bg-blue-600 rounded-lg px-4 py-2 hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {retrying ? 'Queuing…' : 'Retry scrape'}
                      </button>
                    )
                  )}
                  <button
                    onClick={handleArchive}
                    disabled={archiving}
                    className="inline-flex items-center gap-2 text-sm font-medium text-white bg-red-600 rounded-lg px-4 py-2 hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {archiving ? 'Archiving…' : 'Archive'}
                  </button>
                  {urlSaved ? (
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                      URL updated
                    </span>
                  ) : urlEditMode ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="url"
                        value={urlInput}
                        onChange={e => setUrlInput(e.target.value)}
                        placeholder="https://example.com/pricing"
                        className="text-sm border border-gray-300 rounded-lg px-3 py-2 w-72 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <button
                        onClick={handleUpdateUrl}
                        disabled={urlSaving || !urlInput.trim()}
                        className="text-sm font-medium text-white bg-blue-600 rounded-lg px-3 py-2 hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {urlSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={() => { setUrlEditMode(false); setUrlInput(''); }}
                        className="text-sm font-medium text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setUrlEditMode(true); setUrlInput(pricingUrl ?? ''); }}
                      className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors"
                    >
                      Update pricing URL
                    </button>
                  )}
                  {snoozed ? (
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
                      Snoozed 24h
                    </span>
                  ) : (
                    <button
                      onClick={handleSnooze}
                      disabled={snoozing}
                      className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {snoozing ? 'Snoozing…' : 'Snooze 24h'}
                    </button>
                  )}
                </div>
                {actionError && (
                  <p className="text-xs text-red-500 mt-3">{actionError}</p>
                )}
              </div>
            )}

            {/* Audit trail */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Audit Trail</h2>
              {!history?.data.length ? (
                <p className="text-sm text-gray-400 italic">No history found.</p>
              ) : (
                <div className="space-y-1.5">
                  {history.data.map(a => (
                    <div key={a.id} className="flex items-start gap-4 text-xs py-1.5 border-b border-gray-50 last:border-0">
                      <span className="text-gray-400 flex-shrink-0 w-36">{new Date(a.created_at).toLocaleString()}</span>
                      <span className={`flex-shrink-0 px-1.5 py-0.5 rounded font-mono font-medium ${
                        a.stage === 'triage' ? 'bg-purple-50 text-purple-700'
                        : a.stage === 'scrape' ? 'bg-blue-50 text-blue-700'
                        : 'bg-gray-100 text-gray-600'
                      }`}>{a.action_type}</span>
                      <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        a.outcome === 'queued' ? 'bg-blue-100 text-blue-700'
                        : a.outcome === 'escalated' ? 'bg-red-100 text-red-700'
                        : a.outcome === 'ok' ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                      }`}>{a.outcome}</span>
                      {a.reason && <span className="text-gray-500 truncate flex-1">{a.reason}</span>}
                      {a.task_id && (
                        <Link to={`/tasks/${a.task_id}`} className="flex-shrink-0 text-blue-500 hover:underline">task →</Link>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
